import * as Yup from 'yup';
import { formatPhone } from 'utils/helpers';

export const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD', 'MA',
  'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY',
  'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX',
  'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
].map((code) => ({ value: code, label: code }));

export function getInitialValues(attributes) {
  const { first_name = '', middle_name = '', last_name = '', email = '', mobile_phone = '', address_line1 = '', address_line2 = '', city = '', state = '', zip_code = '', emergency_contacts } = attributes || {};

  return {
    first_name,
    middle_name,
    last_name,
    email,
    mobile_phone: mobile_phone ? formatPhone(mobile_phone) : '',
    address_line1,
    address_line2,
    city,
    state,
    zip_code,
    emergency_contacts: emergency_contacts?.data?.length
      ? emergency_contacts.data.map(({ attributes: a }) => ({
          id: a.id,
          first_name: a.first_name || '',
          last_name: a.last_name || '',
          relationship: a.relationship || '',
          phone: a.phone ? formatPhone(a.phone) : '',
        }))
      : [],
  };
}

export const isEmptyEmergencyContact = (c) =>
  !c?.first_name?.trim() &&
  !c?.last_name?.trim() &&
  !c?.relationship?.trim() &&
  !c?.phone?.trim();

export const validationSchema = Yup.object({
  first_name: Yup.string().trim().required('Required'),
  last_name: Yup.string().trim().required('Required'),
  email: Yup.string().trim().email('Must be a valid email').required('Required'),
  mobile_phone: Yup.string().matches(/^\(\d{3}\) \d{3}-\d{4}$/, 'Must be in format (000) 000-0000').required('Required'),
  address_line1: Yup.string().trim().required('Required'),
  city: Yup.string().trim().required('Required'),
  state: Yup.string().required('Required'),
  zip_code: Yup.string().matches(/^\d{5}$/, 'Must be 5 numbers').required('Required'),
  emergency_contacts: Yup.array().of(
    Yup.lazy((contact) =>
      isEmptyEmergencyContact(contact)
        ? Yup.object()
        : Yup.object({
            first_name: Yup.string().trim().required('Required'),
            last_name: Yup.string().trim().required('Required'),
            relationship: Yup.string().trim().required('Required'),
            phone: Yup.string().matches(/^\(\d{3}\) \d{3}-\d{4}$/, 'Must be in format (000) 000-0000').required('Required'),
          })
    )
  ),
});

export const formatGooglePlaces = (places) => {
  const place = places[0];
  if (!place.address_components) return;

  const get = (type: string, short = false) => {
    const comp = place.address_components!.find((c) => c.types.includes(type));
    return comp ? (short ? comp.short_name : comp.long_name) : '';
  };

  const streetNumber = get('street_number');
  const route = get('route');
  const address_line1 = [streetNumber, route].filter(Boolean).join(' ') || place.formatted_address || '';
  const city = get('locality') || get('sublocality_level_1') || get('administrative_area_level_3');
  const state = get('administrative_area_level_1', true);
  const zip_code = get('postal_code');

  return { address_line1, city, state, zip_code }
}
